import { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  Settings, LayoutDashboard, ChevronRight, ChevronDown, Check,
  PanelLeftClose, PanelLeftOpen,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { iconFor } from "@/lib/menuIcons";
import { useUnsavedChangesContext } from "@/context/UnsavedChangesContext";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
  AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";

export function SettingsLayout({ children }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rail, setRail] = useState([]);
  const [groups, setGroups] = useState([]);
  const [open, setOpen] = useState({});
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("mh_settings_panel_collapsed") === "1"; } catch { return false; }
  });

  const openKey = user ? `mh_settings_menu_open_${user.user_id}` : "mh_settings_menu_open";

  // Unsaved-changes guard: intercept navigation while an editor on the current page is dirty.
  const unsaved = useUnsavedChangesContext();
  const dirty = !!unsaved?.dirty;
  const [pending, setPending] = useState(null); // destination route awaiting confirmation
  const go = (to) => { if (dirty) setPending(to); else navigate(to); };
  const onNavLink = (e, to) => { if (dirty) { e.preventDefault(); setPending(to); } else { setPanelCollapsed(true); } };
  const confirmLeave = () => { const to = pending; setPending(null); if (to != null) navigate(to); };

  const setPanelCollapsed = (v) => {
    setCollapsed(v);
    try { localStorage.setItem("mh_settings_panel_collapsed", v ? "1" : "0"); } catch { /* ignore */ }
  };

  useEffect(() => {
    if (!user) return;
    api.get("/menu").then((res) => {
      const items = (res.data.groups || []).flatMap((g) => g.items || []);
      setRail(items);
    }).catch(() => {});
    api.get("/settings-menu").then((res) => {
      const gs = res.data.groups || [];
      setGroups(gs);
      let saved = {};
      try { saved = JSON.parse(localStorage.getItem(openKey) || "{}"); } catch { /* ignore */ }
      const next = {};
      gs.forEach((g) => { next[g.id] = g.id in saved ? saved[g.id] : true; });
      setOpen(next);
    }).catch(() => {});
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleGroup = (id) => setOpen((o) => {
    const next = { ...o, [id]: !o[id] };
    try { localStorage.setItem(openKey, JSON.stringify(next)); } catch { /* ignore */ }
    return next;
  });

  return (
    <div className="flex h-screen overflow-hidden bg-zinc-950 text-zinc-100 font-[Inter]">
      {/* icon rail (minimised main menu) */}
      <div className="w-[68px] flex-shrink-0 border-r border-zinc-800 bg-zinc-950 flex flex-col items-center py-4">
        <div className="h-8 w-8 rounded-md bg-indigo-600 flex items-center justify-center font-black text-white text-sm">D</div>
        <div className="mt-6 flex flex-col gap-1.5">
          <button
            title="Dashboard"
            data-testid="rail-dashboard"
            onClick={() => go("/")}
            className="h-10 w-10 rounded-lg flex items-center justify-center text-zinc-500 hover:text-zinc-100 hover:bg-zinc-800 transition-colors"
          >
            <LayoutDashboard className="h-[18px] w-[18px]" />
          </button>
          {rail.map((r) => {
            const Icon = iconFor(r.icon);
            return (
              <button
                key={r.key}
                title={r.label}
                data-testid={`rail-${r.route === "/" ? "dashboard" : r.route.slice(1)}`}
                onClick={() => go(r.route)}
                className="h-10 w-10 rounded-lg flex items-center justify-center text-zinc-500 hover:text-zinc-100 hover:bg-zinc-800 transition-colors"
              >
                <Icon className="h-[18px] w-[18px]" />
              </button>
            );
          })}
        </div>
        <div className="mt-auto relative">
          <span className="absolute -left-4 top-1/2 -translate-y-1/2 h-6 w-1 rounded-r bg-indigo-500" />
          <div className="h-10 w-10 rounded-lg flex items-center justify-center bg-indigo-600/20 text-indigo-300 border border-indigo-600/40">
            <Settings className="h-[18px] w-[18px]" />
          </div>
        </div>
      </div>

      {/* grouped settings panel — collapses to an icon rail when a link is opened */}
      {collapsed ? (
        <div className="w-[68px] flex-shrink-0 bg-zinc-950 border-r border-zinc-800 flex flex-col items-center py-4" data-testid="settings-menu-collapsed">
          <button
            data-testid="settings-panel-expand"
            title="Expand settings menu"
            onClick={() => setPanelCollapsed(false)}
            className="h-10 w-10 rounded-lg flex items-center justify-center bg-indigo-600/15 text-indigo-300 border border-indigo-600/30 hover:bg-indigo-600/25 transition-colors"
          >
            <PanelLeftOpen className="h-[18px] w-[18px]" />
          </button>
          <div className="mt-4 flex flex-col gap-1.5" data-testid="settings-menu">
            {groups.flatMap((g) => g.items).map((l) => {
              const Icon = iconFor(l.icon);
              return (
                <NavLink
                  key={l.route}
                  to={l.route}
                  title={l.label}
                  onClick={(e) => onNavLink(e, l.route)}
                  data-testid={`settings-rail-${l.route.split("/").pop()}`}
                  className={({ isActive }) =>
                    `h-10 w-10 rounded-lg flex items-center justify-center transition-colors ${isActive ? "bg-indigo-600/20 text-indigo-300 border border-indigo-600/40" : "text-zinc-500 hover:text-zinc-100 hover:bg-zinc-800 border border-transparent"}`
                  }
                >
                  <Icon className="h-[18px] w-[18px]" />
                </NavLink>
              );
            })}
          </div>
          <button
            data-testid="settings-done-btn"
            title="Done — Return to Hub"
            onClick={() => go("/")}
            className="mt-auto h-10 w-10 rounded-lg flex items-center justify-center bg-indigo-600 hover:bg-indigo-500 text-white transition-colors"
          >
            <Check className="h-[18px] w-[18px]" />
          </button>
        </div>
      ) : (
        <div className="w-80 flex-shrink-0 bg-zinc-950 border-r border-zinc-800 flex flex-col">
          <div className="flex items-center gap-2 px-6 pt-5 pb-4">
            <div className="h-9 w-9 rounded-lg bg-indigo-600/15 border border-indigo-600/30 flex items-center justify-center">
              <Settings className="h-4 w-4 text-indigo-300" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-[Outfit] font-bold tracking-normal leading-tight">Settings</p>
              <p className="text-xs text-zinc-500">Administer every module</p>
            </div>
            <button
              data-testid="settings-panel-collapse"
              title="Collapse to icons"
              onClick={() => setPanelCollapsed(true)}
              className="h-8 w-8 rounded-lg flex items-center justify-center text-zinc-500 hover:text-zinc-100 hover:bg-zinc-800 transition-colors"
            >
              <PanelLeftClose className="h-[18px] w-[18px]" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-4 pb-4 space-y-2" data-testid="settings-menu">
            {groups.map((group) => (
              <div key={group.id} data-testid={`settings-group-${group.id}`}>
                <button
                  onClick={() => toggleGroup(group.id)}
                  data-testid={`settings-group-toggle-${group.id}`}
                  className="w-full flex items-center gap-2 px-2 py-2 rounded-lg text-[10px] font-bold tracking-[0.18em] uppercase text-zinc-500 hover:text-zinc-300 transition-colors"
                >
                  <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open[group.id] ? "" : "-rotate-90"}`} />
                  <span className="flex-1 text-left">{group.label}</span>
                </button>
                <AnimatePresence initial={false}>
                  {open[group.id] && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }} className="overflow-hidden space-y-1.5 pb-1"
                    >
                      {group.items.map((l) => {
                        const Icon = iconFor(l.icon);
                        return (
                          <NavLink
                            key={l.route}
                            to={l.route}
                            onClick={(e) => onNavLink(e, l.route)}
                            data-testid={`settings-nav-${l.route.split("/").pop()}`}
                            className={({ isActive }) =>
                              `w-full text-left flex items-start gap-3 p-3 rounded-xl border transition-colors ${isActive ? "bg-zinc-900 border-indigo-600/40" : "bg-zinc-900/40 border-zinc-800 hover:border-zinc-700"}`
                            }
                          >
                            {({ isActive }) => (
                              <>
                                <div className={`h-8 w-8 rounded-lg flex items-center justify-center flex-shrink-0 ${isActive ? "bg-indigo-600/20 text-indigo-300" : "bg-zinc-800 text-zinc-400"}`}>
                                  <Icon className="h-4 w-4" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className={`text-sm font-medium ${isActive ? "text-zinc-50" : "text-zinc-200"}`}>{l.label}</p>
                                  <p className="text-xs text-zinc-500 leading-snug">{l.desc}</p>
                                </div>
                                <ChevronRight className={`h-4 w-4 flex-shrink-0 mt-0.5 ${isActive ? "text-indigo-400" : "text-zinc-600"}`} />
                              </>
                            )}
                          </NavLink>
                        );
                      })}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>

          {/* Done — Return to Hub */}
          <div className="p-4 border-t border-zinc-800">
            <button
              data-testid="settings-done-btn"
              onClick={() => go("/")}
              className="w-full flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white font-medium rounded-lg px-4 py-2.5 transition-colors"
            >
              <Check className="h-4 w-4" />
              Done — Return to Hub
            </button>
          </div>
        </div>
      )}

      {/* content */}
      <div className="flex-1 overflow-y-auto bg-zinc-950">{children}</div>

      <AlertDialog open={pending !== null} onOpenChange={(o) => { if (!o) setPending(null); }}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100" data-testid="unsaved-changes-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved changes</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              You have unsaved changes on this page. If you leave now, those changes will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="unsaved-stay-btn" onClick={() => setPending(null)} className="bg-zinc-800 border-zinc-700 text-zinc-100 hover:bg-zinc-700">
              Stay on page
            </AlertDialogCancel>
            <AlertDialogAction data-testid="unsaved-leave-btn" onClick={confirmLeave} className="bg-red-600 hover:bg-red-500 text-white">
              Leave anyway
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
